import {ChangeDetectorRef, Component, ElementRef, Input, OnInit, ViewChild} from '@angular/core';
import {DashboardContext} from "../../../dashboard/dashboard.context";
import {ChartDataProvider} from "../../../../utils/chart-data-provider";
import {startWith} from 'rxjs';
import {MeasurementDataset} from "../../base-measurement-chart";
import {BuildingType, Location} from "@flowmaps-com/flowmaps-typescriptmodels";
import {EnergyCompassSettings} from "../energy-compass-chart.component";
import {EnergyCompassParisProofLinePlugin} from "../energy-compass-paris-proof-line.plugin";
import {RefdataUtils} from "../../../refdata/refdata-utils";
import {WeiiCalculationOptionId, WeiiCalculationResult} from "../../../../utils/weii-calculation.service";
import {TranslateDirective} from '../../../../common/utils/translate.directive';
import {exportTableToExcel} from '../../../../common/upload/excel.utils';
import {bestLocationEnergyLabel} from '../../../common/energy-label/energy-label.component';

@Component({
    selector: 'app-energy-compass-table',
    templateUrl: './energy-compass-table.component.html',
    styleUrls: ['./energy-compass-table.component.scss']
})
export class EnergyCompassTableComponent implements OnInit {
    context = DashboardContext;
    @Input() dataProvider: ChartDataProvider;
    @Input() energyLabels: string[];
    @Input() settings: EnergyCompassSettings;
    private _visible = false;
    @Input()
    set visible(value: boolean) {
        this._visible = value;
        if (value) {
            this.refreshVisibleRows();
        } else {
            this.visibleRows = [];
        }
    }
    get visible(): boolean {
        return this._visible;
    }
    _data: MeasurementDataset[];
    @ViewChild("table") table: ElementRef;
    visibleRows: any[] = [];
    exportRows: any[] = [];
    renderExportTable = false;

    constructor(private changeDetectorRef: ChangeDetectorRef) {
    }

    ngOnInit(): void {
        this.dataProvider.aggregatedDataChanged.pipe(startWith(this.dataProvider.lastValue)).subscribe(d => {
            this._data = d.datasets.filter(d => !d.dataset.hidden);
            if (this.visible) this.refreshVisibleRows();
        });
    }

    downloadTable(fileName: string, title: string): void {
        this.exportRows = this.records(this._data || []);
        this.renderExportTable = true;
        this.changeDetectorRef.detectChanges();
        try {
            this.exportTable(this.table, fileName, title);
        } finally {
            this.renderExportTable = false;
            this.exportRows = [];
            this.changeDetectorRef.detectChanges();
        }
    }

    protected exportTable(table: ElementRef, fileName: string, title: string): void {
        // This table exists only during export; translate before the asynchronous DOM observer runs.
        const copy = table.nativeElement.cloneNode(true) as HTMLTableElement;
        copy.querySelectorAll('th').forEach(cell =>
            cell.textContent = TranslateDirective.getTranslation(cell.textContent, true) as string);
        copy.querySelectorAll('tbody td').forEach(cell => {
            if (/^-?\d+\.\d+$/.test(cell.textContent.trim())) {
                cell.setAttribute('data-z', '#,##0.########');
            }
        });
        exportTableToExcel(new ElementRef(copy), fileName, title);
    }

    records(dataset: MeasurementDataset[]) {
        return dataset.filter(d => d.dataset["buildingType"])
            .flatMap(d =>
                d.dataset.data.map((r, j) => {
                    const buildingType: BuildingType = d.dataset["buildingType"];
                    const energyLabel = this.energyLabels[r["x"]];
                    const location: Location = r["location"];
                    const calculation: WeiiCalculationResult = r["weiiResult"];
                    return energyLabel === EnergyCompassParisProofLinePlugin.unknownLabel && !this.settings.showUnknown ? null : {
                        locationId: r["locationId"],
                        locationName: r["label"],
                        calculationYear: r["calculationYear"],
                        comparisonYear: d.dataset["comparisonYear"] === true,
                        buildingTypeName: buildingType.info.name,
                        weiiClass: this.efficiencyClass(calculation?.effectiveWeii ?? d.dataset.tooltip.data[j], buildingType),
                        energyLabel: bestLocationEnergyLabel(location),
                        value: calculation?.effectiveWeii ?? d.dataset.tooltip.data[j],
                        parisProofValue: buildingType.info.efficiencyScale.ParisProof,
                        area: RefdataUtils.getLocationArea(location),
                        calculationVariant: this.calculationVariant(calculation),
                        calculationPeriodStart: calculation?.audit.calculation_period?.start,
                        calculationPeriodEnd: calculation?.audit.calculation_period?.end,
                        weatherStationName: location.info?.weatherStation?.name,
                        weatherStationCode: location.info?.weatherStation?.code,
                        standardWeii: calculation?.standardWeii,
                        climateNormalizedWeii: calculation?.climateNormalizationApplied
                            ? calculation.adjustedWeii : null,
                        climateNormalizationApplied: calculation?.climateNormalizationApplied,
                        climateDegreeDays: calculation?.audit.climate_normalization_degree_days,
                        climateReferenceDegreeDays: calculation?.audit.climate_reference_degree_days,
                        climateHeatingShare: calculation?.audit.climate_heating_share,
                        climateCorrectionFactor: calculation?.audit.climate_correction_factor,
                        climateCorrectionKwh: calculation?.audit.climate_correction_kwh,
                        normalizedGasKwh: calculation?.audit.normalized_gas_kwh,
                        normalizedHeatKwh: calculation?.audit.normalized_heat_kwh,
                        grossProductionKwh: calculation?.components.grossProductionKwh,
                        directlySelfConsumedElectricityKwh: calculation?.components.directlySelfConsumedElectricityKwh,
                        netElectricityKwh: calculation?.components.netElectricityKwh,
                        adjustedElectricityKwh: calculation?.components.adjustedElectricityKwh,
                        excludedEvKwh: calculation?.excludedEvKwh,
                        fullElectric: calculation?.audit.is_full_electric,
                        excludedGasM3: calculation?.excludedGasM3,
                        excludedHeatGj: calculation?.excludedHeatGj,
                        excludedColdGj: calculation?.excludedColdGj,
                        heatGj: calculation?.audit.heat_gj,
                        heatWeight: calculation?.audit.heat_weight,
                        weightedHeatKwh: calculation?.audit.heat_weighted_kwh,
                        coldGj: calculation?.audit.cold_gj,
                        coldWeight: calculation?.audit.cold_weight,
                        weightedColdKwh: calculation?.audit.cold_weighted_kwh
                    };
                }))
            .filter(d => d);
    }

    private efficiencyClass(score: number, buildingType: BuildingType): string | null {
        if (score == null || !Number.isFinite(score)) return null;
        const thresholds = Object.entries(buildingType.info.efficiencyScale || {})
            .filter(([, threshold]) => threshold != null && Number.isFinite(threshold))
            .sort((a, b) => a[1] - b[1]);
        const code = thresholds.find(([, threshold]) => score <= threshold)?.[0]
            || (thresholds.some(([key]) => key === 'Uneconomical') ? 'VeryUneconomical' : null);
        return code ? TranslateDirective.getTranslation(code.replace(/([a-z])([A-Z])/g, '$1 $2'), true) as string : null;
    }

    private calculationVariant(calculation: WeiiCalculationResult): string {
        if (!calculation || calculation.appliedOptions.length === 0) {
            return TranslateDirective.getTranslation('Standard WEii', true);
        }
        return calculation.appliedOptions.map(option => option === WeiiCalculationOptionId.IncludeOnsiteGeneration
            ? TranslateDirective.getTranslation('Onsite generation included', true)
            : option === WeiiCalculationOptionId.ExcludeEvCharging
                ? TranslateDirective.getTranslation('EV charging excluded', true)
                : TranslateDirective.getTranslation('Climate normalized', true)).join(', ');
    }

    displayRows(dataset: MeasurementDataset[]): any[] {
        const records = this.records(dataset);
        const primaryRecords = records.filter(record => !record.comparisonYear);
        if (!this.settings?.compareYears) return primaryRecords;
        const comparisonByLocation = new Map(records.filter(record => record.comparisonYear)
            .map(record => [record.locationId, record]));
        return primaryRecords.map(primary => {
            const comparison = comparisonByLocation.get(primary.locationId);
            const delta = comparison == null ? null : primary.value - comparison.value;
            const deltaPercentage = comparison?.value ? delta / comparison.value : null;
            return {...primary, comparison, delta, deltaPercentage};
        });
    }

    private refreshVisibleRows(): void {
        this.visibleRows = this.displayRows(this._data || []);
    }

    get comparisonActive(): boolean {
        return !!this.settings?.compareYears;
    }

    trackBy: any = (index: number, item: any) => {
        return `${item?.locationId}-${item?.calculationYear}`;
    };
}
